import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, Label, FormFeedback, Col, Row, Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import ButtonIcon from '../../common/ButtonIcon';
import CredsModal from '../Vendor/CredsModal';
import DynamicCredentialField from './DynamicCredentialField';
import DynamicPostCredWidget from './DynamicPostCredWidget';
import BZAPIService from '../../../services/BZAPIService';
import { resolveSource } from './schemaTransforms';

/**
 * Credentials form content without TabPane/Card wrappers.
 * Used inside the wizard accordion. The full DynamicCredentialsTab wraps this
 * in TabPane + Card for backward compatibility with static forms.
 */
const DynamicCredentialsContent = React.forwardRef(
  (
    {
      schema,
      vendorId,
      fields,
      handleFieldChange,
      isSubmitted,
      dynamicState,
      setDynamicState,
      onValidCredentials,
      ...restProps
    },
    ref
  ) => {
    const [isValidCredentials, setIsValidCredentials] = useState(false);
    const [isTestingValidCredentials, setIsTestingValidCredentials] = useState(false);
    const [isAddingCredentials, setIsAddingCredentials] = useState(false);

    if (!schema) return null;

    const credFields = schema.fields || [];
    const storedCheckField = schema.storedCheckField;
    const hasStoredCreds = storedCheckField && (fields[storedCheckField] || isValidCredentials);

    async function testCreds() {
      setIsTestingValidCredentials(true);
      try {
        const testConfig = schema.testEndpoint;
        const data = {};
        if (testConfig.payload && typeof testConfig.payload === 'object') {
          Object.entries(testConfig.payload).forEach(([key, source]) => {
            data[key] = resolveSource(source, { fields, ...restProps }, dynamicState);
          });
        } else {
          (testConfig.buildPayload || []).forEach(item => {
            const val = resolveSource(item.source, { fields, ...restProps }, dynamicState);
            if (item.transform === 'boolToInt') {
              data[item.key] = val === false ? 0 : 1;
            } else {
              data[item.key] = val;
            }
          });
        }

        const result = await BZAPIService.vendorTestCreds(vendorId, data);
        setIsTestingValidCredentials(false);

        if (!result) {
          toast.error(testConfig.errorMessage || 'Unable to validate credentials.');
          setIsAddingCredentials(false);
          return;
        }

        let hasError = false;
        (testConfig.onSuccess || []).forEach(action => {
          switch (action.action) {
            case 'checkField':
              if (!result[action.field]) {
                toast.error(action.errorMessage || 'Validation failed.');
                hasError = true;
              }
              break;
            case 'setStateFromResult':
              {
                let resultData = result[action.resultPath || action.resultField];
                // Transform raw arrays into {label, value} objects if format specified
                if (action.format === 'labelValue' && Array.isArray(resultData)) {
                  resultData = resultData.map(item =>
                    typeof item === 'string'
                      ? { label: item, value: item }
                      : { label: item.label || item.name || item, value: item.value || item.id || item }
                  );
                } else if (action.format === 'labelValueWithFields' && Array.isArray(resultData)) {
                  resultData = resultData.map(item => {
                    const label = action.labelTemplate
                      ? action.labelTemplate.replace(/\{(\w+)\}/g, (_, key) => item[key] || '')
                      : item.label || item.name || String(item);
                    const value = action.valueField ? item[action.valueField] : item.value || item.id || item;
                    const extra = {};
                    if (action.extraFields) {
                      action.extraFields.forEach(f => {
                        extra[f] = item[f];
                      });
                    }
                    return { label, value, ...extra };
                  });
                }
                setDynamicState(prev => ({
                  ...prev,
                  [action.stateKey]: resultData
                }));
              }
              break;
            case 'setFieldFromResult':
              {
                const resultKey = action.resultPath || action.resultField;
                const fieldKey = action.fieldId || action.fieldKey;
                if (fields && result[resultKey]) {
                  fields[fieldKey] = result[resultKey];
                }
              }
              break;
            default:
              break;
          }
        });

        if (!hasError && (result.completed !== undefined ? result.completed : true)) {
          setIsValidCredentials(true);
          if (onValidCredentials) onValidCredentials(true);
          setIsAddingCredentials(false);
          toast.success(testConfig.successMessage || 'Credentials validated.');
        } else if (!hasError) {
          toast.error(testConfig.errorMessage || 'Unable to validate credentials.');
          setIsAddingCredentials(false);
        }
      } catch (e) {
        setIsTestingValidCredentials(false);
        setIsAddingCredentials(false);
        toast.error(schema?.testEndpoint?.errorMessage || 'Unable to validate credentials.');
        console.error('Credential test error:', e);
      }
    }

    function handleWidgetChange(stateKey, value) {
      setDynamicState(prev => ({ ...prev, [stateKey]: value }));
    }

    function evalCondition(cond) {
      if (typeof cond === 'string') {
        const val = dynamicState[cond] !== undefined ? dynamicState[cond] : restProps[cond];
        return { available: val !== undefined && val !== null, result: !!val };
      }
      if (typeof cond === 'object' && cond.stateKey) {
        const val = dynamicState[cond.stateKey] !== undefined ? dynamicState[cond.stateKey] : restProps[cond.stateKey];
        const available = val !== undefined && val !== null;
        if (cond.equals !== undefined) return { available, result: val === cond.equals };
        return { available, result: !!val };
      }
      return { available: true, result: true };
    }

    // State 3 (fresh validation): all conditions must be satisfied
    function isWidgetVisible(widget) {
      if (!widget.condition) return true;
      const conditions = Array.isArray(widget.condition) ? widget.condition : [widget.condition];
      return conditions.every(cond => evalCondition(cond).result);
    }

    // State 2 (read-only): skip conditions whose state key isn't available in dynamicState or restProps
    function isWidgetVisibleReadOnly(widget) {
      if (!widget.condition) return true;
      const conditions = Array.isArray(widget.condition) ? widget.condition : [widget.condition];
      return conditions.every(cond => {
        const { available, result } = evalCondition(cond);
        return !available || result;
      });
    }

    function getWidgetOptions(widget) {
      if (widget.optionsSource === 'static') {
        return widget.options || [];
      }
      const optionsKey = widget.optionsSource || widget.optionsKey;
      if (optionsKey) {
        const dynamicOpts = dynamicState[optionsKey] || restProps[optionsKey];
        if (dynamicOpts && widget.optionsTransform === 'awsOrgTransform') {
          return Array.isArray(dynamicOpts)
            ? dynamicOpts.map(o => ({ label: o.name || o.label || o, value: o.id || o.value || o }))
            : [];
        }
        return dynamicOpts || [];
      }
      return widget.options || [];
    }

    const allCredsEmpty = credFields.every(f => !fields[f.id]);

    return (
      <>
        <Row>
          <Col lg="12">
            <FormGroup>
              <Label>
                {schema.title || 'Credentials'}
                <span style={{ color: '#bd472a' }}>*</span>
              </Label>
              {!isAddingCredentials ? (
                <>
                  <div className="d-flex align-items-center">
                    <ButtonIcon
                      onClick={() => setIsAddingCredentials(true)}
                      color="falcon-default"
                      data-testid={'dynamic-set-credentials-btn'}
                      size="sm"
                      icon="plus"
                      iconClassName="fs--2"
                    >
                      Set Credentials
                    </ButtonIcon>

                    {hasStoredCreds ? (
                      <div className="d-flex align-items-center" data-testid={'dynamic-creds-stored-message'}>
                        {'  '}
                        <FontAwesomeIcon icon={'check'} style={{ color: 'green', marginLeft: '10px' }} />{' '}
                        {schema.storedMessage || 'Credentials Stored'}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  {renderFieldRows(credFields, fields, handleFieldChange, isSubmitted)}

                  <CredsModal
                    isTestingValidCredentials={isTestingValidCredentials}
                    setIsTestingCredentials={setIsTestingValidCredentials}
                    fields={fields}
                    testCreds={testCreds}
                    vendorId={vendorId}
                  />
                  <ButtonIcon
                    onClick={() => setIsAddingCredentials(false)}
                    color="falcon-default"
                    size="sm"
                    icon="times"
                    iconClassName="fs--2"
                    style={{ marginRight: '10px' }}
                    data-testid={'dynamic-cancel-creds-btn'}
                  >
                    Cancel
                  </ButtonIcon>
                </>
              )}
              <FormFeedback className={allCredsEmpty && isSubmitted ? 'd-block' : ''} type="invalid">
                <Alert color="danger" fade={false}>
                  Please add required credentials.
                </Alert>
              </FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        {(schema.postCredentialWidgets || []).length > 0 &&
          (() => {
            const hasSavedValues = schema.postCredentialWidgets.some(w => {
              // For toggles/checkboxes, false/0 is a valid saved state — check existence, not truthiness
              if (w.type === 'toggle' || w.type === 'checkbox') {
                return dynamicState[w.stateKey] !== undefined || restProps[w.stateKey] !== undefined;
              }
              const val = dynamicState[w.stateKey] !== undefined ? dynamicState[w.stateKey] : restProps[w.stateKey];
              if (!val) return false;
              if (Array.isArray(val)) return val.length > 0;
              if (typeof val === 'object') return !!val.value || !!val.label;
              return !!val;
            });
            const hasNewOptions = schema.postCredentialWidgets.some(w => {
              // Toggles/checkboxes always have something to render — no options needed
              if (w.type === 'toggle' || w.type === 'checkbox') return true;
              const opts = getWidgetOptions(w);
              return opts && opts.length > 0;
            });

            // State 1: No saved values and no validation done → hide entirely
            if (!hasSavedValues && !isValidCredentials) return null;

            // State 2: Has saved values but NOT re-validating → show read-only
            if (hasSavedValues && !isValidCredentials) {
              return (
                <Row className="mt-2">
                  {schema.postCredentialWidgets.map((widget, i) => {
                    if (!isWidgetVisibleReadOnly(widget)) return null;
                    const isBoolWidget = widget.type === 'toggle' || widget.type === 'checkbox';
                    const savedVal = isBoolWidget
                      ? dynamicState[widget.stateKey] !== undefined
                        ? dynamicState[widget.stateKey]
                        : restProps[widget.stateKey]
                      : dynamicState[widget.stateKey] || restProps[widget.stateKey];
                    if (!isBoolWidget && !savedVal) return null;
                    if (!isBoolWidget && Array.isArray(savedVal) && savedVal.length === 0) return null;
                    if (
                      !isBoolWidget &&
                      typeof savedVal === 'object' &&
                      !savedVal.value &&
                      !savedVal.label &&
                      !Array.isArray(savedVal)
                    )
                      return null;
                    let displayItems;
                    if (isBoolWidget) {
                      displayItems = savedVal ? 'Enabled' : 'Disabled';
                    } else if (Array.isArray(savedVal)) {
                      displayItems = savedVal.map(v => v.label || v.name || String(v)).join(', ');
                    } else if (typeof savedVal === 'object' && savedVal !== null) {
                      displayItems = savedVal.label || savedVal.value || '';
                    } else {
                      displayItems = String(savedVal || '');
                    }
                    return (
                      <Col key={widget.stateKey || i} lg={String(widget.colSize || 6)}>
                        <FormGroup>
                          <Label className="fw-bold">{widget.label}</Label>
                          <div className="border rounded p-2 bg-light text-muted" style={{ minHeight: '38px' }}>
                            {displayItems || <span className="text-muted fst-italic">None selected</span>}
                          </div>
                        </FormGroup>
                      </Col>
                    );
                  })}
                </Row>
              );
            }

            // State 3: Just validated → show editable widgets (only if options are available)
            if (!hasNewOptions) return null;
            return (
              <Row>
                {schema.postCredentialWidgets.map((widget, i) => (
                  <DynamicPostCredWidget
                    key={widget.stateKey || i}
                    widget={widget}
                    value={dynamicState[widget.stateKey] || restProps[widget.stateKey]}
                    options={getWidgetOptions(widget)}
                    onChange={handleWidgetChange}
                    isSubmitted={isSubmitted}
                    isVisible={isWidgetVisible(widget)}
                  />
                ))}
              </Row>
            );
          })()}
      </>
    );
  }
);

function renderFieldRows(credFields, fields, handleFieldChange, isSubmitted) {
  const rows = [];
  let currentRow = [];
  let currentRowSize = 0;

  credFields.forEach(field => {
    const size = field.colSize || 6;
    if (currentRowSize + size > 12) {
      rows.push(currentRow);
      currentRow = [];
      currentRowSize = 0;
    }
    currentRow.push(field);
    currentRowSize += size;
  });
  if (currentRow.length) rows.push(currentRow);

  return rows.map((row, ri) => (
    <Row key={ri}>
      {row.map(field => (
        <DynamicCredentialField
          key={field.id}
          field={field}
          value={fields[field.id]}
          onChange={handleFieldChange}
          isSubmitted={isSubmitted}
        />
      ))}
    </Row>
  ));
}

DynamicCredentialsContent.displayName = 'DynamicCredentialsContent';

DynamicCredentialsContent.propTypes = {
  schema: PropTypes.object.isRequired,
  vendorId: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  handleFieldChange: PropTypes.func.isRequired,
  isSubmitted: PropTypes.bool,
  dynamicState: PropTypes.object,
  setDynamicState: PropTypes.func,
  onValidCredentials: PropTypes.func
};

export default DynamicCredentialsContent;
